import { useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { useUpdateTender } from "../hooks/useTenders";
import { WORK_SCOPE_LABEL } from "../types";
import type { TenderWorkScope } from "../types";

const SCOPE_OPTIONS = Object.entries(WORK_SCOPE_LABEL) as [
  Exclude<TenderWorkScope, "">,
  string,
][];

const SCOPE_COLOR: Record<
  Exclude<TenderWorkScope, "">,
  "blue" | "orange" | "purple"
> = {
  projekt: "blue",
  wykonawstwo: "orange",
  projekt_i_wykonawstwo: "purple",
};

/**
 * Zakres przetargu (projekt / budowa / oba) obok statusu.
 *
 * Nierozpoznany zakres pokazujemy jako wyraźny brak, a nie chowamy — od niego zależy,
 * jakie przedsięwzięcie powstanie po wygranej, więc pusta wartość ma być widoczna i
 * kłopotliwa. Zmiana jest dostępna tylko dla osób, które mogą edytować przetarg;
 * backend i tak jej pilnuje.
 */
export function TenderScopeControl({
  tenderId,
  workScope,
  workScopeSource,
  canEdit,
}: {
  tenderId: string;
  workScope: TenderWorkScope;
  workScopeSource: string;
  canEdit: boolean;
}) {
  const update = useUpdateTender(tenderId);
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <select
        autoFocus
        className="input py-1 text-sm"
        defaultValue={workScope || ""}
        disabled={update.isPending}
        onBlur={() => setEditing(false)}
        onChange={(e) => {
          const value = e.target.value as Exclude<TenderWorkScope, "">;
          if (!value) return;
          update.mutate(
            { work_scope: value },
            { onSettled: () => setEditing(false) },
          );
        }}
      >
        <option value="">— wybierz zakres —</option>
        {SCOPE_OPTIONS.map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
    );
  }

  const body = workScope ? (
    <Badge color={SCOPE_COLOR[workScope]}>{WORK_SCOPE_LABEL[workScope]}</Badge>
  ) : (
    <Badge color="red">Zakres nieustalony</Badge>
  );

  const title = workScope
    ? workScopeSource === "manual"
      ? "Zakres ustawiony ręcznie"
      : "Zakres odczytany z prefiksu nazwy"
    : "Nie udało się odczytać zakresu z nazwy — ustaw ręcznie";

  if (!canEdit) return <span title={title}>{body}</span>;

  return (
    <button
      type="button"
      title={`${title} · kliknij, aby zmienić`}
      className="cursor-pointer"
      onClick={() => setEditing(true)}
    >
      {body}
    </button>
  );
}
